import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { env } from '@/config/env';
import { authSession } from '@/features/auth/stores/auth.store';
import { hostOf } from '@/features/auth/api/auth.mappers';
import { refreshTokens } from './tokenRefresh';
import {
  canRenewViaRefresh,
  ensureZumoToken,
  renewViaRefresh,
  requestZumoToken,
} from '@/lib/medis/zumoToken';
import { medisSession } from '@/lib/medis/medisSession';
import { isMedisSessionInvalid } from '@/lib/medis/envelope';
import { endpointUrls, proxifyMedisUrl, MEDIS_API_NAME } from '@/lib/medis/endpointUrls';
import { endpoints } from './endpoints';

/** MEDIS v1 calls that change server state — never replayed after a transient failure. */
const MEDIS_MUTATING_PATHS: readonly string[] = [
  endpoints.medis.setAppointment,
  endpoints.medis.registerPatient,
];

/**
 * Endpoints that must never carry an Authorization header or trigger refresh-retry.
 * NOTE: /v1/verify-otp and /v1/resend are deliberately NOT here — they verify the
 * signed-in account on the auth server and need the Bearer.
 */
const PUBLIC_PATHS = [
  '/login-password',
  '/register',
  '/refresh',
  '/password-reset/request',
  '/password-reset/reset-with-otp',
  '/exchange-google-token',
  '/exchange-facebook-token',
  '/exchange-apple-token',
  '/maintenance/',
];

function isPublicPath(url?: string): boolean {
  if (!url) return false;
  return PUBLIC_PATHS.some((p) => url.includes(p));
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

interface ZumoConfig extends InternalAxiosRequestConfig {
  /** Whether the request interceptor found an X-ZUMO-AUTH token for this host. */
  _zumoSent?: boolean;
  _retried?: boolean;
}

interface MedisConfig extends InternalAxiosRequestConfig {
  /** Original endpoint path, preserved across retries (config.url gets rewritten). */
  _medisPath?: string;
  /** Force resolution to the static base host instead of the dynamic registry. */
  _forceStatic?: boolean;
  _retried?: boolean;
}

// ─── Bearer-token services (backend + auth) ─────────────────────────────────

function attachAuthHeader(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  if (!isPublicPath(config.url)) {
    const token = authSession.getAccessToken();
    if (token) config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
}

/** On a 401, refresh the JWT once (shared single-flight) and replay the request. */
function installRefreshInterceptor(instance: AxiosInstance): void {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const original = error.config as RetriableConfig | undefined;
      const shouldRefresh =
        error.response?.status === 401 &&
        original &&
        !original._retried &&
        !isPublicPath(original.url) &&
        authSession.getRefreshToken() !== null;

      if (!shouldRefresh) return Promise.reject(error);

      original._retried = true;
      const tokens = await refreshTokens();
      if (!tokens) return Promise.reject(error);

      original.headers.set('Authorization', `Bearer ${tokens.accessToken}`);
      return instance(original);
    },
  );
}

export function createBearerClient(baseURL: string): AxiosInstance {
  const instance = axios.create({
    baseURL,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    timeout: 30_000,
  });
  instance.interceptors.request.use(attachAuthHeader);
  installRefreshInterceptor(instance);
  return instance;
}

// ─── MEDIS v2 service (Azure EasyAuth — X-ZUMO-AUTH, no session, no Bearer) ──

/**
 * Client for MEDIS /v2 endpoints. These sit behind Azure App Service
 * Authentication (EasyAuth), which does NOT accept our OAuth Bearer token and
 * does NOT use the v1 `/NewSession` access-code handshake. The only credential
 * is the Azure session token in the `X-ZUMO-AUTH` header, and a token is only
 * valid on the host that issued it. Tokens are lazy per host: the auth server
 * co-issues one with the OAuth tokens (the `azure` scope) for the registry's
 * production host, and `ensureZumoToken()` fetches any other host's token on
 * demand (`POST /v1/zumo-token`, allow-listed server-side) the first time a
 * call goes there — a registry row pointing elsewhere, a document download
 * URL on another host, stage. Any `Authorization` header is stripped —
 * EasyAuth ignores it and we don't want to leak the access token to the
 * Function app.
 *
 * On a 401 (the host's token expired — it is shorter-lived than the access
 * token — or was never issuable) the token for THAT host is renewed through
 * the same on-demand call and the request replayed once. Two shortcuts: the
 * store already holds a newer token for the host than the one this request
 * sent (it straddled a renewal) — replay with that one, no call; and a host
 * the auth server refused minutes ago is not asked again — the 401 is
 * surfaced at once. Legacy sessions (single token, issued before the per-host
 * model) still go through the OAuth refresh, which upgrades them to the map.
 */
export function createZumoClient(): AxiosInstance {
  const instance = axios.create({
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    timeout: 30_000,
  });

  // EasyAuth tokens are per host: send the one issued by the host this call
  // goes to (v2 URLs are absolute, resolved per call by the dynamic registry),
  // fetching it first when the session has none for that host yet.
  instance.interceptors.request.use(async (config) => {
    const cfg = config as ZumoConfig;
    cfg.headers.delete('Authorization');
    const zumo = await ensureZumoToken(cfg.url);
    if (zumo) cfg.headers.set('X-ZUMO-AUTH', zumo);
    else cfg.headers.delete('X-ZUMO-AUTH'); // a replay must not resend a stale one
    // Remembered for the 401 handler's diagnostics: a 401 without a token is
    // a routing/allow-list gap, not an expired session.
    cfg._zumoSent = Boolean(zumo);
    return cfg;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      // Cancelled by the caller (AbortSignal) — nothing to recover.
      if (axios.isCancel(error)) return Promise.reject(error);

      const original = error.config as ZumoConfig | undefined;
      if (error.response?.status !== 401 || !original || original._retried) {
        return Promise.reject(error);
      }

      // Sent with a token a concurrent renewal has since replaced: the store
      // already holds the newer one for this host, so replay with it rather
      // than ask the auth server again.
      const current = authSession.getZumoToken(original.url);
      if (current && current !== original.headers.get('X-ZUMO-AUTH')) {
        original._retried = true;
        return instance(original); // the request interceptor re-reads the token
      }

      if (!authSession.hasLegacyZumoOnly()) {
        // Per-host session: renew the token of THIS host on demand. `force`
        // (look past the map, which still holds the rejected token, and past
        // a transient memo) only when the request actually carried a token —
        // a 401 on a request sent WITHOUT one is not new evidence, so the
        // memos apply as they would on any call. When the auth server itself
        // could not serve the endpoint (missing route / app error) fall back
        // once to the OAuth refresh, whose grant re-issues the default host.
        // Null means the host is refused or unreachable — surface the 401 (in
        // dev, name the URL so a registry/allow-list mismatch is visible).
        const host = hostOf(original.url);
        if (!host) return Promise.reject(error);
        const sent = original._zumoSent === true;
        let fresh = await requestZumoToken(host, { force: sent });
        if (!fresh && sent && canRenewViaRefresh(host) && authSession.getRefreshToken() !== null) {
          fresh = await renewViaRefresh(host);
        }
        if (!fresh || fresh === original.headers.get('X-ZUMO-AUTH')) {
          if (env.isDev) {
            console.warn(`[zumo] 401 and ${sent ? 'no renewed' : 'no'} X-ZUMO-AUTH token for the host of`, original.url);
          }
          return Promise.reject(error);
        }
        original._retried = true;
        return instance(original);
      }

      // Legacy session (single token): only an OAuth refresh renews it — and
      // the refresh upgrades the session to the host map, so re-read through
      // the lazy path, which also covers a host the grant did not include.
      if (authSession.getRefreshToken() === null) return Promise.reject(error);
      original._retried = true;
      if (!(await refreshTokens()) || !(await ensureZumoToken(original.url))) {
        return Promise.reject(error);
      }
      return instance(original);
    },
  );

  return instance;
}

// ─── MEDIS service (Azure — x-gral-* session headers) ───────────────────────

function createMedisClient(baseURL: string): AxiosInstance {
  const instance = axios.create({
    baseURL,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    timeout: 30_000,
  });

  // Before every call: attach session headers (access code + validator + raw
  // JWT) and resolve the target URL through the dynamic endpoint registry.
  instance.interceptors.request.use(async (config) => {
    const cfg = config as MedisConfig;
    const headers = await medisSession.getHeaders();
    // Data endpoints take ONLY the access code + validator. The raw JWT
    // (x-gral-authorization) is exclusively for /NewSession — sending it here
    // makes MEDIS reject the call (e.g. GetAccessList).
    cfg.headers.delete('x-gral-authorization');
    cfg.headers.set('x-gral-accesscode', headers['x-gral-accesscode']);
    cfg.headers.set('x-gral-accessvalidator', headers['x-gral-accessvalidator']);

    // Resolve against the original path (config.url is rewritten on retries).
    const path = cfg._medisPath ?? cfg.url ?? '';
    cfg._medisPath = path;

    if (cfg._forceStatic) {
      // Fallback: bypass the dynamic registry, use the static base host.
      cfg.url = proxifyMedisUrl(`${env.medisBaseUrl}${path}`);
    } else {
      await endpointUrls.ensureLoaded();
      const apiName = MEDIS_API_NAME[path] ?? '';
      cfg.url = proxifyMedisUrl(endpointUrls.resolve(apiName, path));
    }
    cfg.baseURL = '';
    return cfg;
  });

  // Resilient retry (at most once):
  //  - Session invalid (any method): MEDIS rejected the call before doing
  //    anything, so replaying is safe. Refresh the JWT, drop the cached access
  //    code and retry on the SAME dynamically resolved host, so the new session
  //    and the data call stay on one host.
  //  - Otherwise, only read calls are replayed, and only on network errors /
  //    timeouts / 5xx: on the static base host when the dynamic registry had
  //    routed the call elsewhere, otherwise once more on the same URL. A write
  //    (SetAppointment, RegisterPatient) may have succeeded behind a slow
  //    redirect chain — replaying it could double-book. 4xx business/validation
  //    errors surface to the caller unchanged.
  //  - A request the caller cancelled is never replayed.
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      // Cancelled (AbortSignal / unmounted query): no response either, but the
      // caller gave up on it — never treat it as a transient failure.
      if (axios.isCancel(error)) return Promise.reject(error);

      const original = error.config as MedisConfig | undefined;
      if (!original || original._retried) return Promise.reject(error);

      const status = error.response?.status;
      if (isMedisSessionInvalid(status, error.response?.data)) {
        original._retried = true;
        if (authSession.getRefreshToken()) await refreshTokens();
        medisSession.invalidate();
        return instance(original);
      }

      // MEDIS v1 reads are mostly POSTs (GetAppointments, GetAccessList, …), so
      // idempotency is decided per endpoint, not by HTTP method.
      const idempotent = !MEDIS_MUTATING_PATHS.includes(original._medisPath ?? '');
      const transient = status === undefined || status >= 500;
      if (!idempotent || !transient) return Promise.reject(error);

      // One replay: on the static base host if the registry sent us elsewhere,
      // else on the same URL — the registry's default row is often the static
      // host itself, and a blip there still deserves a second attempt.
      const staticUrl = proxifyMedisUrl(`${env.medisBaseUrl}${original._medisPath ?? ''}`);
      original._retried = true;
      if (original.url !== staticUrl) original._forceStatic = true;
      return instance(original);
    },
  );

  return instance;
}

/** Backend website service (Laravel 10) — login, register, password reset, app data. */
export const backendApi = createBearerClient(env.backendBaseUrl);

/** Auth/OAuth server (Laravel 11 + Passport) — user profile, social account linking. */
export const authApi = createBearerClient(env.authBaseUrl);

/** MEDIS real-time service (Azure) — patients, appointments, results. */
export const medisApi = createMedisClient(env.medisBaseUrl);
